import AuthApiUtils from '../utils/api/auth';
import { addMessage as addModalMessage, hide as hideModal } from './modal';
import {
  addMessage as addFlashMessage,
  clearMessage as clearFlashMessage,
} from './flash';

export const SIGNUP_SENT = 'SIGNUP_SENT';
export const SIGNUP_SUCCEEDED = 'SIGNUP_SUCCEEDED';
export const SIGNUP_FAILED = 'SIGNUP_FAILED';
export const LOGIN_SENT = 'LOGIN_SENT';
export const LOGIN_SUCCEEDED = 'LOGIN_SUCCEEDED';
export const LOGIN_FAILED = 'LOGIN_FAILED';
export const LOGOUT = 'LOGOUT';
export const CHANGE_PASSWORD_SENT = 'CHANGE_PASSWORD_SENT';
export const CHANGE_PASSWORD_SUCCEEDED = 'CHANGE_PASSWORD_SUCCEEDED';
export const CHANGE_PASSWORD_FAILED = 'CHANGE_PASSWORD_FAILED';
export const RESET_PASSWORD_SENT = 'RESET_PASSWORD_SENT';
export const RESET_PASSWORD_SUCCEEDED = 'RESET_PASSWORD_SUCCEEDED';
export const RESET_PASSWORD_FAILED = 'RESET_PASSWORD_FAILED';
export const SEND_RESET_PASSWORD_LINK_REQUESTED =
  'SEND_RESET_PASSWORD_LINK_REQUESTED';
export const SEND_RESET_PASSWORD_LINK_SUCCEEDED =
  'SEND_RESET_PASSWORD_LINK_SUCCEEDED';
export const SEND_RESET_PASSWORD_LINK_FAILED =
  'SEND_RESET_PASSWORD_LINK_FAILED';
export const UPDATE_AFTER_SIGNUP_TO_FALSE = 'UPDATE_AFTER_SIGNUP_TO_FALSE';

function addToLocalStorage(data) {
  localStorage.setItem('authToken', data.authToken);
  localStorage.setItem('profile', data.profile);
  localStorage.setItem('user', JSON.stringify(data.user));

  // Store refresh token and expiration if available (new auth format)
  if (data.refreshToken) {
    localStorage.setItem('refreshToken', data.refreshToken);
  }
  if (data.expiresIn) {
    const expiresAt = Date.now() + data.expiresIn * 1000;
    localStorage.setItem('tokenExpiresAt', expiresAt.toString());
  }
}

function clearLocalStorage() {
  localStorage.removeItem('authToken');
  localStorage.removeItem('profile');
  localStorage.removeItem('user');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('tokenExpiresAt');
}

function dealWithLangChange(data) {
  const localeInfo = data.locale_info;
  if (localeInfo) {
    try {
      document.body.lang = localeInfo.locale.split('-')[0];
    } catch (err) {
      console.log('Attempted to assign to readonly property.');
    }
  }
}

export function signupSent() {
  return { type: SIGNUP_SENT };
}

export function signupSucceeded(data) {
  addToLocalStorage(data);
  // logEvent('Student', 'Created an Account');
  return {
    type: SIGNUP_SUCCEEDED,
    payload: data,
  };
}

export function signupFailed(error) {
  return { type: SIGNUP_FAILED };
}

export function loginSent() {
  return { type: LOGIN_SENT };
}

export function loginSucceeded(data) {
  addToLocalStorage(data);
  return {
    type: LOGIN_SUCCEEDED,
    payload: data,
  };
}

export function loginFailed(error) {
  return { type: LOGIN_FAILED };
}

export function logout() {
  // Attempt to revoke refresh token on server before clearing local storage
  const refreshToken = localStorage.getItem('refreshToken');
  if (refreshToken) {
    // Fire and forget - don't wait for response
    AuthApiUtils.logoutWithToken(refreshToken).catch(() => {
      // Ignore errors - we're logging out anyway
    });
  }

  clearLocalStorage();
  return {
    type: LOGOUT,
    payload: {},
  };
}

export function changePasswordSent() {
  return { type: CHANGE_PASSWORD_SENT };
}

export function changePasswordSucceeded(data) {
  addToLocalStorage(data);
  return {
    type: CHANGE_PASSWORD_SUCCEEDED,
    payload: data,
  };
}

export function changePasswordFailed(error) {
  return { type: RESET_PASSWORD_FAILED };
}

export function resetPasswordSent() {
  return { type: RESET_PASSWORD_SENT };
}

export function resetPasswordSucceeded(data) {
  addToLocalStorage(data);
  return {
    type: RESET_PASSWORD_SUCCEEDED,
    payload: data,
  };
}

export function resetPasswordFailed(error) {
  return { type: RESET_PASSWORD_FAILED };
}

export function updateAfterSignupToFalse() {
  return { type: UPDATE_AFTER_SIGNUP_TO_FALSE };
}

export function signup(params, modal = false) {
  return (dispatch) => {
    dispatch(signupSent());
    return AuthApiUtils.signup(params).then(
      (data) => {
        dispatch(signupSucceeded(data));
        if (modal) {
          dispatch(hideModal());
        } else {
          dispatch(clearFlashMessage());
        }
      },
      (error) => {
        dispatch(signupFailed(error));
        if (modal) {
          const opts = {
            type: 'error',
            autoDisappear: false,
          };
          dispatch(addModalMessage(error.message, opts));
        } else {
          const opts = {
            type: 'error',
            autoDisappear: false,
            sticky: false,
          };
          dispatch(addFlashMessage(error.message, opts));
        }
      },
    );
  };
}

export function login(email, password, countryCode, ip, locale, modal = false) {
  return (dispatch) => {
    dispatch(loginSent());
    return AuthApiUtils.loginWithEmail(
      email,
      password,
      countryCode,
      ip,
      locale,
    ).then(
      (data) => {
        dealWithLangChange(data);
        dispatch(loginSucceeded(data));
        if (modal) {
          dispatch(hideModal());
        } else {
          dispatch(clearFlashMessage());
        }
      },
      (error) => {
        dispatch(loginFailed(error));
        if (modal) {
          const opts = {
            type: 'error',
            autoDisappear: false,
          };
          dispatch(addModalMessage(error.message, opts));
        } else {
          const opts = {
            type: 'error',
            autoDisappear: false,
            sticky: false,
          };
          dispatch(addFlashMessage(error.message, opts));
        }
      },
    );
  };
}

export function changePassword(oldPassword, newPassword, confirmPassword) {
  return (dispatch) => {
    dispatch(changePasswordSent());
    return AuthApiUtils.changePassword(
      oldPassword,
      newPassword,
      confirmPassword,
    ).then(
      (data) => {
        dispatch(changePasswordSucceeded(data));
        dispatch(
          addFlashMessage(data.message, {
            autoDisappear: true,
            type: 'success',
            sticky: true,
          }),
        );
      },
      (error) => {
        dispatch(changePasswordFailed(error));
        dispatch(
          addFlashMessage(error.message, {
            autoDisappear: false,
            type: 'error',
            sticky: true,
          }),
        );
      },
    );
  };
}

export function resetPassword(
  newPassword,
  confirmPassword,
  resetPasswordToken,
) {
  return (dispatch) => {
    dispatch(resetPasswordSent());
    return AuthApiUtils.resetPassword(
      newPassword,
      confirmPassword,
      resetPasswordToken,
    ).then(
      (data) => {
        dispatch(resetPasswordSucceeded(data));
        dispatch(
          addFlashMessage(data.message, {
            autoDisappear: false,
            type: 'success',
            sticky: false,
          }),
        );
      },
      (error) => {
        dispatch(resetPasswordFailed(error));
        dispatch(
          addFlashMessage(error.message, {
            autoDisappear: false,
            type: 'error',
            sticky: false,
          }),
        );
      },
    );
  };
}

export function sendResetPasswordLinkRequested() {
  return { type: SEND_RESET_PASSWORD_LINK_REQUESTED };
}

export function sendResetPasswordLinkFailed() {
  return { type: SEND_RESET_PASSWORD_LINK_FAILED };
}

export function sendResetPasswordLinkSucceeded(data) {
  return {
    type: SEND_RESET_PASSWORD_LINK_SUCCEEDED,
    payload: data,
  };
}

export function sendResetPasswordLink(email) {
  return (dispatch) => {
    dispatch(sendResetPasswordLinkRequested());
    return AuthApiUtils.sendResetPasswordLink(email).then(
      (data) => {
        const opts = {
          type: 'success',
          autoDisappear: false,
        };
        dispatch(sendResetPasswordLinkSucceeded(data));
        dispatch(addModalMessage(data.message, opts));
      },
      (error) => {
        const opts = {
          type: 'error',
          autoDisappear: false,
        };
        dispatch(sendResetPasswordLinkFailed());
        dispatch(addModalMessage(error.message, opts));
      },
    );
  };
}
